import type { RefObject } from 'react';
import { useRef } from 'react';

import { toCurrencyTruncated } from '@/utilities/formatting';
import { KeyMetric } from '@/components/KeyMetric';
import { CompanyIntrinsicDiscount } from '@/components/CompanyIntrinsicDiscount';
import RefreshOutline from '@simplywallst/ui-core/icons/RefreshOutline';

import type { NarrativeValuatorCompany } from '../../../../hooks/narrativeValuatorCompanyFragment';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';
import { SetFairValue } from '../../../SetFairValue';

export type Props = {
  authorFairValueLabel: string;
  company: NarrativeValuatorCompany;
  fairValue: number;
  onReset: () => void;
  narrativeId?: string;
  containerRef: RefObject<HTMLDivElement>;
};

export const NarrativeFairValue = ({
  authorFairValueLabel,
  company,
  fairValue,
  onReset,
  narrativeId,
  containerRef,
}: Props) => {
  const fairValueRef = useRef(fairValue);
  const currencyIso =
    getTradingItemCurrencyISO(company.data.currencyInfo) ?? 'XXX';

  return (
    <div className="rounded-t-x1 bg-p-grey-30">
      <div className="grid h-12 grid-cols-[max-content_max-content] justify-between px-x2 pt-x2">
        <div className="grid grid-cols-[max-content_max-content] items-center gap-x1">
          <span className="text-xs font-strong text-solid">Fair Value</span>
          {fairValueRef.current !== fairValue && (
            <button
              type="button"
              onClick={onReset}
              className="focus:outline-solid grid grid-cols-[max-content_max-content] items-center gap-x0_25 text-xs text-brand-01 hover:underline focus:outline-dashed focus:outline-1 focus:outline-offset-[3px] focus:outline-p-blue-100"
            >
              <RefreshOutline className="h-x2 w-auto fill-current" />
              <span>Reset</span>
            </button>
          )}
        </div>
        {narrativeId && (
          <SetFairValue
            companyId={company.id}
            narrativeId={narrativeId}
            containerRef={containerRef}
          />
        )}
      </div>
      <div className="grid gap-x2 p-x2">
        <KeyMetric>
          <div className="grid grid-cols-[max-content_auto] items-center gap-x2">
            <div className="text-mega font-strong text-solid">
              {toCurrencyTruncated(fairValue, 1, currencyIso)}
            </div>
            <div className="text-tiny text-solid">
              <CompanyIntrinsicDiscount
                lastSharePrice={company.analysisValue?.lastSharePrice ?? null}
                npvPerShare={company.analysisValue?.npvPerShare ?? null}
                fairValue={fairValue}
              />
            </div>
          </div>
          <div className="text-xs text-soft">
            {fairValueRef.current !== fairValue
              ? `You've changed Fair Value`
              : authorFairValueLabel}
          </div>
        </KeyMetric>
      </div>
    </div>
  );
};
